import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { trackError } from 'backend-client-web';
import { ExtensionAvailableInfoMessage, IsExtensionAvailableMessage } from 'persistable-types';

import { useCachedExtensionAvailableInfoMessage } from './localStorage/useLocalStorageState';
import postMessageWithResponse, { TimeoutError } from './postMessageWithResponse';

interface ExtensionStateContextValue {
  extensionAvailableInfoMessage?: ExtensionAvailableInfoMessage;
  cachedExtensionAvailableInfoMessage?: ExtensionAvailableInfoMessage;
  update: () => Promise<ExtensionAvailableInfoMessage>;
  setUpdateInterval: (id: string, interval_ms: number | undefined) => void;
  triggerUpdate: () => void;
}

const ExtensionStateContext = createContext<ExtensionStateContextValue>({
  update: () => {
    return new Promise((_, reject) => {
      reject(new Error('ExtensionStateContext not initialized, this should never happen'));
    });
  },
  triggerUpdate: () => {},
  setUpdateInterval: () => {},
});

const EXTENSION_RESPONSE_TIMEOUT_MS = 2_000;

export function ExtensionStateContextProvider({ children }: PropsWithChildren<{}>) {
  const [extensionAvailableInfoMessage, setExtensionAvailableInfoMessage] =
    useState<ExtensionAvailableInfoMessage>();
  const [timedOut, setTimedOut] = useState(false);

  const update = useCallback(async () => {
    // see content/index.ts in ext
    const msg: IsExtensionAvailableMessage = { type: 'isExtensionAvailable' };
    try {
      const response = await postMessageWithResponse(
        tryParseExtensionAvailableInfoMessage,
        EXTENSION_RESPONSE_TIMEOUT_MS,
        msg,
      );

      setExtensionAvailableInfoMessage(response);
      setTimedOut(false);

      return response;
    } catch (e) {
      if (e instanceof TimeoutError) {
        setTimedOut(true);
        setExtensionAvailableInfoMessage(undefined);
      }
      throw e;
    }
  }, []);

  // update extensionInfo on ExtensionAvailableInfoMessage
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.source !== window) {
        return;
      }
      const parsedMessage = tryParseExtensionAvailableInfoMessage(event.data);
      if (!parsedMessage) {
        return;
      }
      setExtensionAvailableInfoMessage(parsedMessage);
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const triggerUpdate = useCallback(() => {
    update().catch((e) => {
      if (e instanceof TimeoutError) {
        return;
      }
      trackError(e);
    });
  }, [update]);

  // send a request to get the extension installed message once when the context is mounted
  useEffect(triggerUpdate, [triggerUpdate]);

  // use a local storage cache to supplement with the last known value
  const [cachedExtensionAvailableInfoMessage, setCachedExtensionAvailableInfoMessage] =
    useCachedExtensionAvailableInfoMessage();
  useEffect(() => {
    if (extensionAvailableInfoMessage === undefined && !timedOut) {
      return;
    }
    setCachedExtensionAvailableInfoMessage(extensionAvailableInfoMessage);
  }, [extensionAvailableInfoMessage, setCachedExtensionAvailableInfoMessage, timedOut]);

  const [updateIntervals, setUpdateIntervals] = useState<Record<string, number>>({});

  const setUpdateInterval = useCallback((id: string, interval_ms: number | undefined) => {
    setUpdateIntervals((oldIntervals) => {
      if (!interval_ms) {
        const { [id]: _drop, ...newIntervals } = oldIntervals;
        return newIntervals;
      }

      return { ...oldIntervals, [id]: interval_ms };
    });
  }, []);

  useEffect(() => {
    if (Object.values(updateIntervals).length === 0) {
      return;
    }
    const minInterval = Math.min(...Object.values(updateIntervals));
    const timerId = window.setInterval(triggerUpdate, minInterval);
    return () => window.clearInterval(timerId);
  }, [triggerUpdate, update, updateIntervals]);

  return (
    <ExtensionStateContext.Provider
      value={{
        extensionAvailableInfoMessage,
        cachedExtensionAvailableInfoMessage,
        update,
        setUpdateInterval,
        triggerUpdate,
      }}
    >
      {children}
    </ExtensionStateContext.Provider>
  );
}

export function useExtensionStateContext() {
  return useContext(ExtensionStateContext);
}

// Tries to parse an ExtensionAvailableInfoMessage from the given event.
// Returns undefined for other events, throws on parsing error
function tryParseExtensionAvailableInfoMessage(
  eventData: unknown,
): ExtensionAvailableInfoMessage | undefined {
  // discard other messages immediately to avoid parsing them unnecessarily
  if (
    typeof eventData !== 'object' ||
    eventData === null ||
    !('type' in eventData) ||
    eventData.type !== 'extensionAvailableInfo'
  ) {
    return undefined;
  }
  return ExtensionAvailableInfoMessage.parse(eventData);
}
